import { Link } from "react-router-dom";
import { ArrowRight, Truck, Zap, Tractor, Wrench, Shield, Clock, DollarSign, Leaf } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1650986422034-10f5000358d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxUZXhhcyUyMHJ1cmFsJTIwcm9hZCUyMHBpbmUlMjB0cmVlcyUyMGVxdWlwbWVudCUyMHRyYWlsZXJ8ZW58MHx8fHwxNzc2NTQwNTA2fDA&ixlib=rb-4.1.0&q=85";
const STORY_IMG = "https://images.unsplash.com/photo-1761699217307-72dbc753f731?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxjcmVlayUyMHdvb2RzJTIwbmF0dXJlJTIwc3VubGlnaHQlMjBwYXRofGVufDB8fHx8MTc3NjU0MDUwMHww&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { name: "Trailers", icon: Truck, desc: "Utility trailers for every hauling need", color: "bg-[#E68A00]/10 text-[#E68A00]" },
  { name: "Generators", icon: Zap, desc: "Reliable power wherever you need it", color: "bg-[#1A2F24]/10 text-[#1A2F24]" },
  { name: "Tractors", icon: Tractor, desc: "Heavy-duty machines for big jobs", color: "bg-[#E68A00]/10 text-[#E68A00]" },
  { name: "Tractor Attachments", icon: Wrench, desc: "Brushhogs, blades, buckets & more", color: "bg-[#1A2F24]/10 text-[#1A2F24]" },
  { name: "Lawn Care", icon: Leaf, desc: "Mowers and lawn maintenance equipment", color: "bg-[#E68A00]/10 text-[#E68A00]" },
];

const FEATURES = [
  { icon: Shield, title: "Reliable Equipment", desc: "Every piece is inspected and maintained before each rental." },
  { icon: Clock, title: "Flexible Rentals", desc: "Daily and weekly rates to fit your project timeline." },
  { icon: DollarSign, title: "Fair Pricing", desc: "Transparent pricing with refundable deposits. No hidden fees." },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Creek Bottom Rentals equipment"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl fade-up">
            <p className="text-[#E68A00] text-sm font-semibold uppercase tracking-[0.15em] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Family-Owned &middot; Piney Woods, Texas
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="hero-heading"
            >
              The Right Tools<br />
              <span className="text-[#E68A00]">For Every Job</span>
            </h1>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
              Trailers, generators, tractors and more. Rent quality equipment at honest prices from our family ranch in the Piney Woods of East Texas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/equipment"
                data-testid="hero-browse-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-8 py-3.5 rounded-md transition-colors pulse-glow"
              >
                Browse Equipment <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#about"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-8 py-3.5 rounded-md transition-colors backdrop-blur-sm border border-white/20"
              >
                Our Story
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 fade-up">
            <p className="text-[#E68A00] text-sm font-semibold uppercase tracking-[0.1em] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              What We Offer
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1A2F24] tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Equipment Categories
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((cat, i) => (
              <Link
                key={cat.name}
                to={`/equipment?category=${encodeURIComponent(cat.name)}`}
                data-testid={`category-card-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`equipment-card bg-white border border-[#E5E0D8] rounded-lg p-6 text-left fade-up fade-up-delay-${i + 1}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${cat.color}`}>
                  <cat.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-[#1C1C1C] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {cat.name}
                </h3>
                <p className="text-sm text-[#595959]">{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-[#EFECE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FEATURES.map((feat, i) => (
              <div key={feat.title} className={`text-center fade-up fade-up-delay-${i + 1}`}>
                <div className="w-14 h-14 bg-[#E68A00] rounded-full flex items-center justify-center mx-auto mb-5">
                  <feat.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {feat.title}
                </h3>
                <p className="text-sm text-[#595959] max-w-xs mx-auto">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / STORY */}
      <section id="about" className="py-20 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="fade-up">
              <p className="text-[#E68A00] text-sm font-semibold uppercase tracking-[0.1em] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Our Story
              </p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1A2F24] tracking-tight mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="about-heading"
              >
                Rooted in the<br />Piney Woods of Texas
              </h2>
              <p className="text-[#595959] leading-relaxed mb-4">
                Creek Bottom Rentals grew out of our family ranch &amp; RV park, nestled deep in the Piney Woods of East Texas. What started as neighbors borrowing our equipment turned into a full-fledged rental business built on trust and handshake values.
              </p>
              <p className="text-[#595959] leading-relaxed mb-6">
                We're family owned and operated &mdash; always have been, always will be. Every piece of equipment is maintained like it's our own, because out here, your word and your work are what matter most. Whether you need a trailer for a weekend project or a tractor to clear land, we've got you covered.
              </p>
              <Link
                to="/equipment"
                data-testid="about-cta"
                className="inline-flex items-center gap-2 text-[#E68A00] hover:text-[#CC7A00] font-semibold text-sm transition-colors"
              >
                See Our Equipment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="fade-up fade-up-delay-2">
              <div className="relative">
                <img
                  src={STORY_IMG}
                  alt="Our story - family"
                  className="rounded-lg w-full h-auto object-cover shadow-lg"
                />
                <div className="absolute -bottom-4 -left-4 bg-[#E68A00] text-white px-6 py-3 rounded-md shadow-lg">
                  <span className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Piney Woods</span>
                  <span className="text-sm ml-2 opacity-80">Texas</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A2F24]">
        <div className="max-w-3xl mx-auto px-4 text-center fade-up">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-lg mx-auto">
            Browse our equipment, pick your dates, and book online. It's that simple.
          </p>
          <Link
            to="/equipment"
            data-testid="cta-browse-btn"
            className="inline-flex items-center gap-2 bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-10 py-4 rounded-md transition-colors text-lg"
          >
            Browse Equipment <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
